import { Box, chakra } from '@chakra-ui/react';
import NextImage from 'next/image';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArticleStatus,
  createArticleImageRelativeUrl,
} from '../common/libs/article';
import { sendAnalyticsEvent } from '../common/libs/googleAnalytics';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';

/**
 * スライド画像
 *
 * 変換完了のステータスではない場合、ステータスに応じた画像を表示する。
 */
function ArticleImage({
  articleId,
  articleType,
  status,
  topImageM,
  updateDate,
  alt,
  onShowThumbnail,
}: {
  articleId: string;
  articleType?: string | null;
  status: ArticleStatus | null;
  topImageM: string | null;
  updateDate: string;
  alt: string | null;
  onShowThumbnail?: (articleId: string) => void;
}) {
  let imageUrl: string | null;
  const updateDateEpochMilliseconds = new Date(updateDate).getTime();
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const [videoWrapperHeight, setVideoWrapperHeight] = useState(0);

  useEffect(() => {
    if (videoWrapperRef.current?.offsetWidth) {
      setVideoWrapperHeight((videoWrapperRef.current.offsetWidth / 16) * 9);
    }
  }, [videoWrapperRef.current?.offsetWidth]);

  const handleShowThumbnail = useCallback(() => {
    onShowThumbnail?.(articleId);
  }, [articleId, onShowThumbnail]);
  const thumbnailRef = useIntersectionObserver(handleShowThumbnail);

  switch (status) {
    case 'DONE':
      imageUrl = createArticleImageRelativeUrl(articleId, topImageM, {
        version: updateDateEpochMilliseconds,
      });
      break;
    case 'CONVERTING':
      imageUrl = '/images/placeholder_article_converting.png';
      break;
    case 'DELETED':
      imageUrl = '/images/placeholder_article_deleted.png';
      break;
    case 'ERROR':
    default:
      imageUrl = '/images/placeholder_article_error.png';
      break;
  }

  const image = (
    <Box ref={thumbnailRef}>
      <NextImage
        src={imageUrl || ''}
        alt={alt || ''}
        layout="fill"
        objectFit="contain"
      />
    </Box>
  );
  if (articleType === 'VIDEO') {
    return (
      <Box
        ref={videoWrapperRef}
        height={videoWrapperHeight}
        width="100%"
        position="relative"
        bg="blackAlpha.300"
        borderBottomColor="gray.50"
        borderBottomWidth={1}
      >
        {image}
        <Box pos="absolute" bottom={0.5} left={1.5} lineHeight={0}>
          <NextImage
            src="/images/icon_video_play_button.svg"
            width={48}
            height={48}
          />
        </Box>
      </Box>
    );
  } else {
    return image;
  }
}

export default ArticleImage;
